import { createFileRoute, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2, TriangleAlert } from 'lucide-react';
import { DocPage } from '@/pages/docs/DocPage';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/runs/$executionId'
)({
  component: ExecutionDocPage,
});

const ExecutionDocPage = () => {
  const { executionId } = useParams({ strict: false }) as { executionId: string };

  // Prefer cloned doc linked to this execution
  const docByExecutionQuery = useQuery({
    ...trpc.workflow.getDocByExecution.queryOptions({ executionId }),
  });

  // Fetch execution to derive doc/topic context for DocPage
  const executionQuery = useQuery({
    ...trpc.workflow.getExecutionById.queryOptions({ id: executionId }),
  });

  const execution = executionQuery.data as any;
  const workflow = execution?.workflow;
  const flow = execution?.flow;

  // Prefer workflow doc, fallback to flow topic first doc
  const fallbackDocId = workflow?.doc?.id || flow?.topic?.docs?.[0]?.id || null;
  const fallbackTopicId = workflow?.doc?.topicId || flow?.topicId || null;

  const docId = docByExecutionQuery.data?.docId || fallbackDocId;
  const topicId = docByExecutionQuery.data?.topicId || fallbackTopicId;
  const isClone = docByExecutionQuery.data?.source === 'clone';

  if (executionQuery.isLoading || docByExecutionQuery.isLoading) {
    return (
      <div className='flex h-screen w-full items-center justify-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-6 w-6 animate-spin' />
      </div>
    );
  }

  if (executionQuery.error) {
    return (
      <div className='flex h-screen w-full flex-col items-center justify-center gap-2 text-sm text-red-500'>
        <TriangleAlert className='h-5 w-5' />
        Failed to load execution data: {executionQuery.error.message}
      </div>
    );
  }

  if (docByExecutionQuery.error) {
    return (
      <div className='flex h-screen w-full flex-col items-center justify-center gap-2 text-sm text-red-500'>
        <TriangleAlert className='h-5 w-5' />
        Failed to resolve document: {docByExecutionQuery.error.message}
      </div>
    );
  }

  if (!docId) {
    return (
      <div className='flex h-screen w-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground'>
        <TriangleAlert className='h-5 w-5' />
        This execution is not linked to a document, so details cannot be displayed.
      </div>
    );
  }

  return (
    <DocPage
      showHeaderRight={false}
      showSidebar={false}
      showPropertiesPanel={true}
      docId={docId}
      topicId={topicId || undefined}
      key={`${docId}-${isClone ? 'clone' : 'orig'}`}
      executionId={executionId}
      viewMode='execution'
      isClone={docByExecutionQuery.data?.source}
    />
  );
};
